import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Download, Clock, Music, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DownloadHistory = ({ onClose, onRedownload }) => {
  const [downloads, setDownloads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [downloading, setDownloading] = useState(null);
  const limit = 20;

  useEffect(() => {
    fetchHistory();
  }, [page]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/downloads/history-detailed?limit=${limit}&skip=${page * limit}`,
        { withCredentials: true }
      );
      setDownloads(response.data.downloads);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching download history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedownload = async (track) => {
    if (!track.drive_link) {
      alert('Este track ya no está disponible para descarga');
      return;
    }
    
    try {
      setDownloading(track.track_id);
      
      // Register the download
      await api.post(
        `/downloads`,
        { track_id: track.track_id },
        { withCredentials: true }
      );
      
      // Open Google Drive link
      window.open(track.drive_link, '_blank');
      
      // Refresh history
      fetchHistory();
      
      if (onRedownload) onRedownload(track);
    } catch (error) {
      console.error('Error redownloading:', error);
      // Still try to open the link even if registration fails
      if (track.drive_link) {
        window.open(track.drive_link, '_blank');
      }
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="download-history-modal">
      <div className="bg-[#0D0D0D] border border-[#262626] rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-[#1A1A1A] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#00D9FF]/10 flex items-center justify-center">
              <Clock className="w-6 h-6 text-[#00D9FF]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Historial de Descargas
              </h2>
              <p className="text-sm text-[#666]">{total} descargas en total</p>
            </div>
          </div>
          <Button
            onClick={onClose}
            className="text-[#666] hover:text-white bg-[#141414] hover:bg-[#1A1A1A] w-10 h-10 rounded-xl p-0"
            data-testid="close-history-btn"
          >
            ✕
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-[#0A0A0A]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#00D9FF]"></div>
            </div>
          ) : downloads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#141414] flex items-center justify-center mb-4">
                <Download className="w-8 h-8 text-[#333]" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No hay descargas aún</h3>
              <p className="text-sm text-[#666]">Tus descargas aparecerán aquí</p>
            </div>
          ) : (
            <div className="space-y-2">
              {downloads.map((item, index) => (
                <div
                  key={`${item.download_id}-${index}`}
                  className="flex items-center gap-4 p-4 bg-[#141414] rounded-2xl hover:bg-[#1A1A1A] transition-all group border border-[#262626] hover:border-[#333]"
                  data-testid={`download-item-${index}`}
                >
                  {/* Track Icon */}
                  <div className="w-12 h-12 rounded-xl bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
                    <Music className="w-5 h-5 text-[#00D9FF]" />
                  </div>

                  {/* Track Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">
                      {item.track.title}
                    </p>
                    <p className="text-sm text-[#888] truncate">
                      {item.track.artist}
                    </p>
                    <div className="flex items-center gap-3 mt-1">
                      {item.track.bpm && (
                        <span className="text-xs text-[#00D9FF]">{item.track.bpm} BPM</span>
                      )}
                      {item.track.key && (
                        <span className="text-xs text-[#00D9FF]">{item.track.key}</span>
                      )}
                      {item.track.version && (
                        <span className="text-xs text-[#888] bg-[#333] px-2 py-0.5 rounded">{item.track.version}</span>
                      )}
                      {item.track.folder && (
                        <span className="text-xs text-[#666]">{item.track.folder}</span>
                      )}
                    </div>
                  </div>

                  {/* Download Date */}
                  <div className="text-right flex-shrink-0 hidden sm:block">
                    <p className="text-xs text-[#666]">Descargado</p>
                    <p className="text-sm text-[#888]">{formatDate(item.downloaded_at)}</p>
                  </div>

                  {/* Re-download Button */}
                  <Button
                    onClick={() => handleRedownload(item.track)}
                    disabled={downloading === item.track.track_id || !item.track.drive_link}
                    className="bg-[#262626] hover:bg-[#333] text-white rounded-lg px-4 py-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    data-testid={`redownload-btn-${index}`}
                  >
                    {downloading === item.track.track_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-[#1A1A1A] flex items-center justify-between bg-[#0D0D0D]">
            <Button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="text-[#B3B3B3] hover:text-white bg-[#141414] hover:bg-[#1A1A1A] disabled:opacity-50 rounded-xl px-4"
            >
              ← Anterior
            </Button>
            <span className="text-sm text-[#666]">
              Página {page + 1} de {totalPages}
            </span>
            <Button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              className="text-[#B3B3B3] hover:text-white bg-[#141414] hover:bg-[#1A1A1A] disabled:opacity-50 rounded-xl px-4"
            >
              Siguiente →
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadHistory;
